import React, { useEffect, useMemo, useState } from 'react';
import { Table, TableProps, Modal, Input, Select, Button } from 'antd';
import dayjs from 'dayjs';
import { getRequest, deleteRequest, ResponseDataType } from 'api';
import { ExclamationCircleFilled, SearchOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { StudentImportModal } from './StudentImportModal';
import { StudentInfo } from '../../Information';
import { EditStudent } from '../../Edit';
import { CreateStudent } from '../../Create';
import { ClassLevelSelect, DatePicker } from 'Components';
import { compareVietnameseNames } from 'utils';

const { confirm } = Modal;
const { Option } = Select;

interface StudentDataType {
    studentId: number;
    bhytNum: string;
    cmnd: string;
    gender: number;
    name: string;
    studentCode: string;
    dob: string;
    email: string;
    address: string;
    parentPhonenum: string;
    className?: string;
    classLevel?: string;
}

export const InactiveStudents = () => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
    const [selectedStudentId, setSelectedStudentId] = useState<number | undefined>();
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [selectedStudentForEdit, setSelectedStudentForEdit] = useState<StudentDataType | null>(null);
    const [studentList, setStudentList] = useState<StudentDataType[]>([]);
    const [classList, setClassList] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalItems, setTotalItems] = useState<number>(0);

    const [searchName, setSearchName] = useState<string>('');
    const [searchCccd, setSearchCccd] = useState<string>('');
    const [searchParentPhone, setSearchParentPhone] = useState<string>('');
    const now = dayjs();
    const defaultYear = now.month() < 7 ? now.subtract(1, 'year') : now;
    const [selectedYear, setSelectedYear] = useState<any>(defaultYear);
    const [searchClassLevel, setSearchClassLevel] = useState<string | undefined>(undefined);
    const [searchClass, setSearchClass] = useState<number | undefined>(undefined);
    const [searchedClass, setSearchedClass] = useState<number | undefined>(undefined);

    const filteredClassList = useMemo(() => {
        if (!searchClassLevel) return classList;
        return classList.filter(cls => cls.classLevel === searchClassLevel);
    }, [classList, searchClassLevel]);

    const columns: TableProps<StudentDataType>['columns'] = [
        { title: 'STT', key: 'stt', align: 'center', render: (_, __, index) => index + 1 },
        { title: 'CCCD', dataIndex: 'cmnd', key: 'cmnd', align: 'center' },
        {
            title: 'Họ và tên',
            dataIndex: 'name',
            key: 'name',
            align: 'left',
            sorter: (a, b) => compareVietnameseNames(a.name, b.name),
            sortDirections: ['ascend', 'descend', 'ascend'],
        },
        { title: 'Ngày sinh', dataIndex: 'dob', key: 'dob', align: 'center' },
        {
            title: 'Giới tính',
            dataIndex: 'gender',
            key: 'gender',
            align: 'center',
            render: (gender) => (gender === 1 ? 'Nam' : 'Nữ'),
        },
        { title: 'Khối', dataIndex: 'classLevel', key: 'classLevel', align: 'center' },
        { title: 'Lớp', dataIndex: 'className', key: 'className', align: 'center' },
        { title: 'Mã BHYT', dataIndex: 'bhytNum', key: 'bhytNum', align: 'center' },
        { title: 'Địa chỉ', dataIndex: 'address', key: 'address', align: 'center' },
        {
            title: 'SĐT phụ huynh',
            dataIndex: 'parentPhonenum',
            key: 'parentPhonenum',
            align: 'center',
            render: (phone) => phone || '-',
        },
        {
            title: 'Hành động',
            dataIndex: 'studentId',
            key: 'action',
            align: 'center',
            render: (studentId, record) => (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                    <span
                        style={{ color: '#1890ff', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        onClick={() => {
                            setSelectedStudentId(studentId);
                            setIsDetailModalOpen(true);
                        }}
                    >
                        Chi tiết
                    </span>
                    <span
                        style={{ color: '#52c41a', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        onClick={() => {
                            setSelectedStudentForEdit(record);
                            setIsEditModalOpen(true);
                        }}
                    >
                        Sửa
                    </span>
                    <span
                        style={{ color: 'red', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        onClick={() => showDeleteConfirm(studentId)}
                    >
                        Xóa
                    </span>
                </div>
            ),
        },
    ];

    const fetchClasses = async () => {
        const schoolId = localStorage.getItem("schoolId");
        if (!schoolId) return;

        const currentYear = selectedYear ? selectedYear.year().toString() : dayjs().year().toString();

        const url = `/cms/class/get-class-years-by-start-end-schoolId?schoolId=${schoolId}&start=${currentYear}&page=1&perPage=1000`;
        const response: ResponseDataType<{ list: any[] }> = await getRequest(url);
        if (response?.code === 200) {
            setClassList(response.info?.list || []);
        }
    };

    const fetchInactiveStudents = async () => {
        const schoolId = localStorage.getItem("schoolId");
        if (!schoolId) {
            toast.error("Không tìm thấy schoolId trong localStorage");
            return;
        }

        const currentYear = selectedYear ? selectedYear.year().toString() : dayjs().year().toString();

        let url = `/cms/student/get-students-by-school?schoolId=${schoolId}&status=0&page=1&perPage=1000&limit=1000&pageSize=1000&size=1000`;
        if (searchName) url += `&name=${encodeURIComponent(searchName)}`;
        if (searchCccd) url += `&studentCode=${encodeURIComponent(searchCccd)}`;
        if (searchParentPhone?.trim() && searchParentPhone.trim().length >= 9) {
            url += `&parentPhone=${encodeURIComponent(searchParentPhone.trim())}`;
        }
        if (searchClassLevel) url += `&classLevel=${encodeURIComponent(searchClassLevel)}`;
        if (searchClass) url += `&classYearId=${searchClass}`;
        url += `&startYear=${currentYear}`;

        setSearchedClass(searchClass);

        const response: ResponseDataType<{ list: StudentDataType[], total: number }> = await getRequest(url);

        if (response?.code === 200) {
            const students = response.info?.list || [];
            setStudentList(students);
            const info = response.info as any;
            setTotalItems(info?.total || info?.totalRecords || info?.count || students.length || 0);
        } else {
            toast.error(response?.msg || 'Lỗi khi tải danh sách học sinh');
            setStudentList([]);
        }
    };

    const handleSearch = () => {
        if (searchParentPhone?.trim() && searchParentPhone.trim().length < 9) {
            toast.warning("Vui lòng nhập từ 9 chữ số trở lên để tìm kiếm theo SĐT phụ huynh");
            return;
        }
        fetchInactiveStudents();
    };

    const showDeleteConfirm = (studentId: number) => {
        confirm({
            title: 'Bạn có chắc chắn muốn xóa học sinh này?',
            icon: <ExclamationCircleFilled />,
            content: 'Bạn sẽ không thể hoàn tác sau khi xóa.',
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            async onOk() {
                const url = `/cms/student/${studentId}`;
                const response: ResponseDataType<{}> = await deleteRequest(url);
                if (response?.code === 200) {
                    toast.success('Xóa học sinh thành công');
                    fetchInactiveStudents();
                } else {
                    toast.error(response?.msg || 'Xóa thất bại');
                }
            },
        });
    };

    useEffect(() => {
        fetchInactiveStudents();
    }, []);

    useEffect(() => {
        setSearchClassLevel(undefined);
        setSearchClass(undefined);
        fetchClasses();
    }, [selectedYear]);

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Input
                        placeholder="Tên học sinh"
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        onPressEnter={handleSearch}
                        style={{ width: '250px' }}
                    />
                    <Input
                        placeholder="Mã CCCD"
                        value={searchCccd}
                        onChange={(e) => setSearchCccd(e.target.value)}
                        onPressEnter={handleSearch}
                        style={{ width: '140px' }}
                    />
                    <Input
                        placeholder="SĐT phụ huynh"
                        value={searchParentPhone}
                        onChange={(e) => setSearchParentPhone(e.target.value)}
                        onPressEnter={handleSearch}
                        allowClear
                        style={{ width: '150px' }}
                    />
                    <ClassLevelSelect
                        style={{ width: '130px' }}
                        placeholder="Chọn khối"
                        value={searchClassLevel}
                        onChange={(value) => {
                            setSearchClassLevel(value);
                            if (searchClass) {
                                const currentCls = classList.find(c => c.classYearId === searchClass);
                                if (currentCls && currentCls.classLevel !== value) {
                                    setSearchClass(undefined);
                                }
                            }
                        }}
                    />
                    <Select
                        style={{ width: '140px' }}
                        placeholder="Chọn lớp"
                        value={searchClass}
                        onChange={(value) => setSearchClass(value)}
                        allowClear
                    >
                        {filteredClassList.map(cls => (
                            <Option key={cls.classYearId} value={cls.classYearId}>{cls.className}</Option>
                        ))}
                    </Select>
                    <DatePicker
                        picker="year"
                        allowClear={false}
                        format={(val) => `${val.year()}-${val.year() + 1}`}
                        value={selectedYear}
                        onChange={(date) => setSelectedYear(date)}
                        placeholder="Chọn năm học"
                        style={{ width: '120px' }}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        Tìm kiếm
                    </Button>
                    {searchedClass && (
                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', fontWeight: 500, color: '#1890ff' }}>
                            Sĩ số: {classList.find(c => c.classYearId === searchedClass)?.studentCount || 0} học sinh
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button type="primary" onClick={() => setIsCreateModalOpen(true)}>
                        Thêm học sinh
                    </Button>
                    <Button type="primary" onClick={() => setIsImportModalOpen(true)}>
                        Tải file Excel
                    </Button>
                </div>
            </div>
            <Table
                columns={columns}
                dataSource={studentList}
                rowKey="studentId"
                pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                }}
            />
            <Modal
                title="Thêm học sinh"
                open={isCreateModalOpen}
                onCancel={() => setIsCreateModalOpen(false)}
                footer={null}
                width={720}
                centered
                destroyOnClose
            >
                <CreateStudent
                    isModal
                    onSuccess={() => {
                        fetchInactiveStudents();
                        fetchClasses();
                    }}
                    onClose={() => setIsCreateModalOpen(false)}
                />
            </Modal>
            <StudentImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onSuccess={() => handleSearch()}
            />
            <Modal
                title="Chi tiết học sinh"
                open={isDetailModalOpen}
                onCancel={() => {
                    setIsDetailModalOpen(false);
                    setSelectedStudentId(undefined);
                }}
                footer={null}
                width={1250}
                destroyOnClose
            >
                <div style={{ maxHeight: '75vh', overflowY: 'auto', paddingRight: '8px' }}>
                    {selectedStudentId && <StudentInfo studentId={selectedStudentId} />}
                </div>
            </Modal>
            <Modal
                title="Chỉnh sửa thông tin học sinh"
                open={isEditModalOpen}
                onCancel={() => {
                    setIsEditModalOpen(false);
                    setSelectedStudentForEdit(null);
                }}
                footer={null}
                width={700}
                destroyOnClose
            >
                {selectedStudentForEdit && (
                    <EditStudent
                        studentData={selectedStudentForEdit}
                        onSuccess={() => fetchInactiveStudents()}
                        onClose={() => {
                            setIsEditModalOpen(false);
                            setSelectedStudentForEdit(null);
                        }}
                    />
                )}
            </Modal>
        </div>
    );
};
